import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { TextField, Select, MenuItem, FormControl, InputLabel, Button, Box, Typography } from '@mui/material';
// @ts-ignore
import regenPodImage from '../../assets/Images/RegenPod.png';
// @ts-ignore
import paulLeeImage from '../../assets/Images/PaulLee.jpg';
// @ts-ignore
import urbanHotelImage from '../../assets/Images/urban-hotel.webp';
import '../../assets/styles/regenpod/after-party.scss';

const validationSchema = Yup.object({
  name: Yup.string().required('Full name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  phone: Yup.string().required('Phone number is required'),
  guestName: Yup.string().when('bringGuest', {
    is: 'yes',
    then: (schema) => schema.required('Guest name is required'),
    otherwise: (schema) => schema
  })
});

export const AfterPartyRegistration: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitResult, setSubmitResult] = useState<{success: boolean, message?: string} | null>(null);

  const handleSubmit = async (values: any) => {
    setIsLoading(true);
    try {
      const response = await fetch('https://api.regenphd.com/after-party-registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({...values, eventType: 'AfterParty'})
      });
      const result = await response.json();
      setSubmitResult({success: response.ok, message: result.message});
      setIsSubmitted(true);
    } catch (error) {
      console.error('Registration failed:', error);
      setSubmitResult({success: false, message: 'Registration failed. Please try again.'});
      setIsSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Regen Pod Launch After Party | Join the Celebration</title>
        <meta name="description" content="Register for the exclusive Regen Pod Launch After Party. Celebrate the future of regenerative wellness with Professor Paul Lee and the team." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="after-party-container">
        <div className="party-hero">
          <div className="hero-content">
            <div className="hero-badge font-semibold">EXCLUSIVE INVITATION</div>
            <h1 className="hero-title font-bold">
              Regen Pod Launch<br />
              <span className="highlight">After Party</span>
            </h1>
            <p className="hero-subtitle font-medium">
              Celebrate the Future of Regeneration
            </p>
            <div className="hero-description">
              <p className="intro-text">
                Join us for an exclusive after-party celebration following the groundbreaking launch of the Regen PhD Pod. 
                Connect with fellow wellness enthusiasts, enjoy refreshments, and experience the community that's shaping 
                the future of regenerative health.
              </p>
            </div>
          </div>
          <div className="hero-image">
            <img src={regenPodImage} alt="Regen PhD Pod" />
          </div>
        </div>

        <div className="host-section">
          <div className="host-card">
            <div className="host-image">
              <img src={paulLeeImage} alt="Professor Paul Lee" />
            </div>
            <div className="host-info">
              <h3 className="font-semibold">Hosted by Professor Paul Lee</h3>
              <p>Join the creator of the Regen PhD Pod for an exclusive celebration of regenerative wellness innovation.</p>
            </div>
          </div>
        </div>

        <div className="event-highlights">
          <div className="highlight-item">
            <div className="highlight-text">
              <strong className="font-semibold">Exclusive Celebration</strong>
              Join the launch team and VIP guests for an unforgettable evening
            </div>
          </div>
          <div className="highlight-item">
            <div className="highlight-text">
              <strong className="font-semibold">Premium Refreshments</strong>
              Enjoy carefully curated food and beverages throughout the evening
            </div>
          </div>
          <div className="highlight-item">
            <div className="highlight-text">
              <strong className="font-semibold">Accommodation Available</strong>
              Optional overnight stay for out-of-town guests
            </div>
          </div>
          <div className="highlight-item">
            <div className="highlight-text">
              <strong className="font-semibold">Networking Opportunity</strong>
              Connect with industry leaders and wellness pioneers
            </div>
          </div>
        </div>

        <div className="hotel-accommodation-section">
          <div className="hotel-container">
            <h2 className="hotel-title font-semibold">Hotel Accommodation</h2>
            <div className="hotel-info">
              <div className="hotel-image">
                <img src={urbanHotelImage} alt="The Urban Hotel Grantham" style={{width: '100%', maxWidth: '400px', borderRadius: '8px'}} />
              </div>
              <div className="hotel-details">
                <h3 className="font-medium">The Urban Hotel Grantham</h3>
                <p className="hotel-description">
                  Located in the heart of Grantham, The Urban Hotel offers comfortable accommodation 
                  just minutes away from The Keep Clinic event venue.
                </p>
                
                <div className="hotel-code-section">
                  <div className="code-display">
                    <h4 className="font-bold" style={{fontSize: '2rem', textAlign: 'center', margin: '20px 0', color: '#2c5aa0'}}>REGENPARTY</h4>
                  </div>
                </div>
                
                <div className="hotel-offer">
                  <div className="offer-badge">EXCLUSIVE DISCOUNT FOR EVENT GUESTS</div>
                  <div className="pricing">
                    <div className="price-item">
                      <span className="price">£58</span>
                      <span className="description">per room</span>
                    </div>
                    <div className="price-item">
                      <span className="price">£70</span>
                      <span className="description">with breakfast</span>
                    </div>
                  </div>
                </div>
                
                <div className="booking-info">
                  <h4 className="font-semibold">How to Book:</h4>
                  <div className="booking-steps">
                    <div className="step"><span className="step-arrow">→</span> Call on number: <a href="tel:01476593000" className="contact-link font-semibold">01476 593000</a></div>
                    <div className="step"><span className="step-arrow">→</span> Say {" "} <strong> REGENPARTY </strong> {" "} code</div>
                    <div className="step"><span className="step-arrow">→</span> Book the hotel</div>
                  </div>
                  <div className="contact-details">
                    <div className="contact-item">
                      <span className="label">🌐 Website:</span>
                      <a href="https://www.urbanhotelgrantham.co.uk" target="_blank" rel="noopener noreferrer" className="contact-link">
                        urbanhotelgrantham.co.uk
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="registration-section">
          <div className="registration-header">
            <h2 className="font-bold">Register for After Party</h2>
            <p>Secure your spot at this exclusive celebration. Limited spaces available.</p>
          </div>

          {!isSubmitted ? (
            <Formik
              initialValues={{
                name: '',
                email: '',
                phone: '',
                bringGuest: 'no',
                guestName: '',
                drinkAlcohol: 'no',
                allergies: '',
                needRoom: 'no'
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, handleChange }) => (
                <Form className="registration-form">
                  <Box className="form-section">
                    <Typography variant="h6" className="section-title font-semibold">Personal Information</Typography>
                    <Box className="form-grid">
                      <Field
                        as={TextField}
                        name="name"
                        label="Full Name"
                        variant="outlined"
                        fullWidth
                        required
                        error={touched.name && !!errors.name}
                        helperText={touched.name && errors.name}
                      />
                      <Field
                        as={TextField}
                        name="email"
                        label="Email Address"
                        type="email"
                        variant="outlined"
                        fullWidth
                        required
                        error={touched.email && !!errors.email}
                        helperText={touched.email && errors.email}
                      />
                      <Field
                        as={TextField}
                        name="phone"
                        label="Phone Number"
                        variant="outlined"
                        fullWidth
                        required
                        error={touched.phone && !!errors.phone}
                        helperText={touched.phone && errors.phone}
                      />
                    </Box>
                  </Box>

                  <Box className="form-section">
                    <Typography variant="h6" className="section-title font-semibold">Event Preferences</Typography>
                    <Box className="form-grid">
                      <FormControl fullWidth>
                        <InputLabel>Bringing someone along?</InputLabel>
                        <Field
                          as={Select}
                          name="bringGuest"
                          label="Bringing someone along?"
                          value={values.bringGuest}
                          onChange={handleChange}
                        >
                          <MenuItem value="no">No, just me</MenuItem>
                          <MenuItem value="yes">Yes, bringing a guest</MenuItem>
                        </Field>
                      </FormControl>
                      
                      {values.bringGuest === 'yes' && (
                        <Field
                          as={TextField}
                          name="guestName"
                          label="Guest Name"
                          variant="outlined"
                          fullWidth
                          error={touched.guestName && !!errors.guestName}
                          helperText={touched.guestName && errors.guestName}
                        />
                      )}
                      
                      <FormControl fullWidth>
                        <InputLabel>Do you drink alcohol?</InputLabel>
                        <Field
                          as={Select}
                          name="drinkAlcohol"
                          label="Do you drink alcohol?"
                          value={values.drinkAlcohol}
                          onChange={handleChange}
                        >
                          <MenuItem value="no">No</MenuItem>
                          <MenuItem value="yes">Yes</MenuItem>
                        </Field>
                      </FormControl>
                      
                      <FormControl fullWidth>
                        <InputLabel>Need accommodation?</InputLabel>
                        <Field
                          as={Select}
                          name="needRoom"
                          label="Need accommodation?"
                          value={values.needRoom}
                          onChange={handleChange}
                        >
                          <MenuItem value="no">No, I don't need a room</MenuItem>
                          <MenuItem value="yes">Yes, I need accommodation</MenuItem>
                        </Field>
                      </FormControl>
                    </Box>
                  </Box>

                  <Box className="form-section">
                    <Typography variant="h6" className="section-title font-semibold">Additional Information</Typography>
                    <Field
                      as={TextField}
                      name="allergies"
                      label="Allergies or dietary requirements"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Please list any allergies or dietary requirements (optional)"
                    />
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    className="submit-button font-semibold"
                    fullWidth
                    disabled={isLoading}
                  >
                    {isLoading ? 'Registering...' : 'Register for After Party'}
                  </Button>
                </Form>
              )}
            </Formik>
          ) : (
            <div className="confirmation-card">
              <div className={`confirmation-icon ${submitResult?.success ? 'success' : 'error'}`}>
                {submitResult?.success ? '✓' : '✗'}
              </div>
              <h3>{submitResult?.success ? 'Registration Confirmed!' : 'Registration Failed'}</h3>
              <p>
                {submitResult?.success 
                  ? 'Thank you for registering! We look forward to celebrating with you at the Regen Pod Launch After Party. You will receive a confirmation email shortly with event details.'
                  : submitResult?.message || 'Something went wrong. Please try again or contact us directly.'
                }
              </p>
              {!submitResult?.success && (
                <button 
                  className="retry-btn"
                  onClick={() => {
                    setIsSubmitted(false);
                    setSubmitResult(null);
                  }}
                >
                  Try Again
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};